
import { useEffect, useState, useCallback } from 'react';
import { useAuthSession } from './useAuthSession';
import { checkSuspiciousActivity, terminateOtherSessions } from '@/utils/securityMonitoring';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { logSecurityEvent } from '@/utils/securityUtils';

interface SecurityMonitoringOptions {
  enabled?: boolean;
  checkInterval?: number; // in milliseconds
  onSuspiciousActivity?: () => void;
}

/**
 * Hook to monitor for suspicious activity and protect user sessions
 */
export function useSecurityMonitoring({
  enabled = true,
  checkInterval = 60000, // Default: check every minute
  onSuspiciousActivity
}: SecurityMonitoringOptions = {}) {
  const { session, userId } = useAuthSession();
  const navigate = useNavigate();
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [detectedSuspiciousActivity, setDetectedSuspiciousActivity] = useState(false);
  
  // Function to check for suspicious activity
  const checkActivity = useCallback(async () => {
    if (!enabled || !userId || !session?.access_token) return;
    
    try {
      const isSuspicious = await checkSuspiciousActivity(userId);
      
      if (isSuspicious) {
        setDetectedSuspiciousActivity(true);
        
        // Log security event
        await logSecurityEvent(
          userId,
          "suspicious_activity_detected",
          "Security monitoring detected suspicious activity",
          { timestamp: new Date().toISOString() }
        );
        
        // Notify user
        toast.error("Suspicious activity detected on your account", {
          description: "For your protection, we've secured your account.",
          duration: 8000,
        });
        
        // Optionally terminate other sessions
        if (session.access_token) {
          await terminateOtherSessions(userId, session.access_token);
        }
        
        // Call custom handler if provided
        if (onSuspiciousActivity) {
          onSuspiciousActivity();
        }
      }
    } catch (error) {
      console.error("Error in security monitoring:", error);
    }
  }, [enabled, userId, session, onSuspiciousActivity]);
  
  // Run periodic checks when component is mounted
  useEffect(() => {
    if (!enabled || !userId) return;
    
    setIsMonitoring(true);
    
    // Initial check
    checkActivity();
    
    // Set up interval for checking
    const intervalId = setInterval(checkActivity, checkInterval);
    
    // Clean up on unmount
    return () => {
      clearInterval(intervalId);
      setIsMonitoring(false);
    };
  }, [enabled, userId, checkInterval, checkActivity]);
  
  // Provide function to manually force a security check
  const forceSecurityCheck = useCallback(() => {
    return checkActivity();
  }, [checkActivity]);
  
  // Provide a function to lock the account and redirect to auth
  const lockAccount = useCallback(async () => {
    if (!userId) return;
    
    try {
      await logSecurityEvent(
        userId,
        "account_locked",
        "Account locked due to security concerns"
      );
      
      // Log the user out
      await navigate('/auth', { replace: true });
      
      toast.error("Your account has been locked for security reasons", {
        description: "Please contact support for assistance.",
        duration: 10000,
      });
    } catch (error) {
      console.error("Error locking account:", error);
    }
  }, [userId, navigate]);
  
  return {
    isMonitoring,
    detectedSuspiciousActivity,
    forceSecurityCheck,
    lockAccount
  };
}
